import React, { useEffect, useState } from 'react'
import axios from 'axios'
import ExcelJS from 'exceljs'
import {
  FaDownload, FaCheckCircle, FaTimesCircle, FaSearch,
  FaExclamationTriangle, FaInfoCircle, FaExpand
} from 'react-icons/fa'
import { IoShieldCheckmark } from 'react-icons/io5'
import ImageZoomModal from './ImageZoomModal.jsx'

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL

// ---------------------------------------------------------------------------
// Drawing Preview — fetches thumbnail, opens an in-app zoomable viewer on click
// ---------------------------------------------------------------------------
function DrawingPreview({ fileId }) {
  const [imgSrc, setImgSrc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [zoomOpen, setZoomOpen] = useState(false)
  const [fullImg, setFullImg] = useState(null)
  const [resolutionLabel, setResolutionLabel] = useState(null)
  const token = sessionStorage.getItem('userToken')

  useEffect(() => {
    if (!fileId) { setLoading(false); return }
    let cancelled = false
    axios.get(`${BASE_URL}/ocr/file-thumbnail/${fileId}/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (!cancelled && res.data.thumbnail_base64) {
        setImgSrc(res.data.thumbnail_base64)
      }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [fileId, token])

  const openFullScreen = async () => {
    if (!fileId && !imgSrc) return

    // Try to fetch high-res image from backend; fall back to thumbnail
    let hiRes = imgSrc
    if (fileId) {
      try {
        const res = await axios.get(`${BASE_URL}/ocr/file-full-image/${fileId}/?max_side=4096`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.data.image_base64) {
          hiRes = res.data.image_base64
          setResolutionLabel(`${res.data.width} x ${res.data.height} px`)
        }
      } catch (e) { /* fall back to thumbnail */ }
    }

    if (!hiRes) return
    setFullImg(hiRes)
    setZoomOpen(true)
  }

  if (loading) {
    return (
      <div className="w-full h-48 bg-gray-100 rounded-lg animate-pulse flex items-center justify-center">
        <span className="text-xs text-gray-400">Loading drawing...</span>
      </div>
    )
  }

  if (!imgSrc) {
    return (
      <div className="w-full h-32 bg-gray-50 rounded-lg flex items-center justify-center border border-dashed border-gray-200">
        <span className="text-xs text-gray-400">No drawing preview available</span>
      </div>
    )
  }

  return (
    <div>
      <div
        className="relative group cursor-pointer rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
        onClick={openFullScreen}
      >
        <img
          src={imgSrc}
          alt="Drawing"
          className="w-full max-h-72 object-contain"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow">
            <FaExpand className="w-4 h-4 text-gray-700" />
          </div>
        </div>
      </div>
      <ImageZoomModal
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        src={fullImg}
        title="Drawing"
        resolutionLabel={resolutionLabel}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Metrics helpers
// ---------------------------------------------------------------------------
function computeMetrics(data) {
  const strata = {
    'S1 Title Block': data.document_meta,
    'S2 Identification': data.identification,
    'S3 Geometry': data.geometry,
    'S4 Material': data.material,
    'S5 Process': data.process,
    'S6 Dimensions': data.dim_summary,
    'S7 Notes': data.notes_summary,
    'S8 BOM': data.bom_summary,
  }

  let totalFields = 0, foundFields = 0
  let highCount = 0, mediumCount = 0, lowCount = 0
  const stratumCoverage = {}

  Object.entries(strata).forEach(([name, fields]) => {
    if (!fields || typeof fields !== 'object') {
      stratumCoverage[name] = { found: 0, total: 0, pct: 0, fields: [] }
      return
    }
    const fieldDetails = []
    Object.entries(fields).forEach(([fieldName, env]) => {
      if (!env || typeof env !== 'object' || !('status' in env)) return
      fieldDetails.push({
        name: fieldName,
        value: env.value,
        unit: env.unit,
        status: env.status,
        confidence_tier: env.confidence_tier,
        text_evidence: env.source?.text_evidence || env.text_evidence,
      })
    })
    const total = fieldDetails.length
    const found = fieldDetails.filter(f => f.status !== 'NotFound').length
    totalFields += total
    foundFields += found
    fieldDetails.forEach(f => {
      if (f.confidence_tier === 'HIGH') highCount++
      else if (f.confidence_tier === 'MEDIUM') mediumCount++
      else if (f.confidence_tier === 'LOW') lowCount++
    })
    stratumCoverage[name] = { found, total, pct: total > 0 ? Math.round((found / total) * 100) : 0, fields: fieldDetails }
  })

  const completeness = totalFields > 0 ? Math.round((foundFields / totalFields) * 100) : 0

  return { totalFields, foundFields, completeness, highCount, mediumCount, lowCount, stratumCoverage }
}

// ---------------------------------------------------------------------------
// Confidence Gauge
// ---------------------------------------------------------------------------
function ConfidenceGauge({ level }) {
  const colors = {
    high: { ring: 'border-green-500', bg: 'bg-green-50', text: 'text-green-700' },
    medium: { ring: 'border-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
    low: { ring: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700' },
  }
  const style = colors[String(level).toLowerCase()] || colors.low

  return (
    <div className={`flex flex-col items-center justify-center p-4 rounded-xl ${style.bg} border-2 ${style.ring}`}>
      <div className={`w-16 h-16 rounded-full border-4 ${style.ring} flex items-center justify-center mb-2`}>
        <span className={`text-lg font-bold ${style.text}`}>{String(level || '?').toUpperCase()}</span>
      </div>
      <span className="text-xs font-medium text-gray-600">Overall Confidence</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Completeness Bar
// ---------------------------------------------------------------------------
function CompletenessBar({ pct, label }) {
  const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'
  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-gray-600">{label}</span>
        <span className="text-xs font-semibold text-gray-800">{pct}%</span>
      </div>
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stratum Coverage Panel — expandable per-stratum field detail
// ---------------------------------------------------------------------------
function StratumCoveragePanel({ stratumCoverage }) {
  const [expanded, setExpanded] = useState({})

  const toggle = (name) => setExpanded(prev => ({ ...prev, [name]: !prev[name] }))

  return (
    <div>
      <span className="text-xs text-gray-600 block mb-2">Stratum Coverage</span>
      <div className="space-y-1">
        {Object.entries(stratumCoverage).map(([name, { found, total, pct, fields }]) => (
          <div key={name}>
            <button
              onClick={() => toggle(name)}
              className="w-full flex items-center gap-2 py-1 hover:bg-gray-50 rounded transition-colors group"
            >
              <svg
                className={`w-3 h-3 text-gray-400 transition-transform ${expanded[name] ? 'rotate-90' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-[10px] text-gray-600 w-24 truncate text-left" title={name}>{name}</span>
              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${pct >= 80 ? 'bg-green-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-500 w-10 text-right">{found}/{total}</span>
            </button>

            {expanded[name] && fields && fields.length > 0 && (
              <div className="ml-5 mt-1 mb-2 border-l-2 border-gray-100 pl-3 space-y-1">
                {fields.map((f) => (
                  <div key={f.name} className="flex items-center gap-2 text-[11px]">
                    {f.status !== 'NotFound'
                      ? <FaCheckCircle className="text-green-500 flex-shrink-0" size={10} />
                      : <FaTimesCircle className="text-red-400 flex-shrink-0" size={10} />
                    }
                    <span className="text-gray-600 w-28 truncate font-medium" title={f.name}>
                      {f.name.replace(/_/g, ' ')}
                    </span>
                    {f.status !== 'NotFound' ? (
                      <>
                        <span className="text-gray-800 truncate flex-1" title={String(f.value ?? '')}>
                          {String(f.value ?? '—')}{f.unit ? ` ${f.unit}` : ''}
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                          f.confidence_tier === 'HIGH' ? 'bg-green-100 text-green-700' :
                          f.confidence_tier === 'MEDIUM' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {f.confidence_tier || '?'}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-400 italic flex-1">Not found</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {expanded[name] && (!fields || fields.length === 0) && (
              <div className="ml-5 mt-1 mb-2 text-[10px] text-gray-400 italic">No fields extracted</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// CSV Export — Vertical table: Label | Value
// ---------------------------------------------------------------------------
function buildCSVRows(data) {
  const rows = [['Section', 'Field', 'Value']]
  const esc = (v) => String(v ?? '').replace(/\n/g, ' ')

  // Helper: push envelope fields as individual rows
  function pushEnvelopes(section, fields) {
    if (!fields || typeof fields !== 'object') return
    Object.entries(fields).forEach(([k, env]) => {
      if (!env || typeof env !== 'object') return
      const val = env.status === 'NotFound' ? 'NOT FOUND'
        : `${esc(env.value)}${env.unit ? ' ' + env.unit : ''} [${env.confidence_tier || '?'}]`
      rows.push([section, k.replace(/_/g, ' '), val])
    })
  }

  // S1 Title Block
  pushEnvelopes('Title Block', data.document_meta)

  // S2 Identification
  pushEnvelopes('Identification', data.identification)

  // S3 Geometry
  pushEnvelopes('Geometry', data.geometry)

  // S4 Material
  pushEnvelopes('Material', data.material)

  // S5 Process
  pushEnvelopes('Process', data.process)

  // General Tolerance
  if (data.general_tolerance) {
    const gt = data.general_tolerance
    rows.push(['Tolerancing', 'general tolerance', gt.status === 'NotFound' ? 'NOT FOUND' : esc(gt.value)])
  }

  // S6 Dimensions — all in one field, semicolon-separated
  const dims = data.dimensions || []
  if (dims.length > 0) {
    const dimStr = dims.map(d => {
      let s = `${d.nominal || '?'}${d.unit ? ' ' + d.unit : ''}`
      if (d.tolerance_plus || d.tolerance_minus) s += ` +${d.tolerance_plus || '?'}/-${d.tolerance_minus || '?'}`
      if (d.type) s += ` (${d.type})`
      return s
    }).join('; ')
    rows.push(['Dimensions', `all dimensions (${dims.length})`, dimStr])
  }

  // GD&T Frames — one field
  const gdt = data.gdt_frames || []
  if (gdt.length > 0) {
    const gdtStr = gdt.map(g => {
      let s = `${g.symbol || '?'} Ø${g.tolerance || '?'}`
      if (g.datum_primary) s += ` |${g.datum_primary}`
      if (g.datum_secondary) s += `|${g.datum_secondary}`
      if (g.datum_tertiary && g.datum_tertiary !== 'null') s += `|${g.datum_tertiary}`
      if (g.affected_feature) s += ` → ${g.affected_feature}`
      return s
    }).join('; ')
    rows.push(['Dimensions', `GD&T frames (${gdt.length})`, gdtStr])
  }

  // Dim summary envelopes
  pushEnvelopes('Dimensions Summary', data.dim_summary)

  // S7 Notes — full text
  const notes = data.notes || []
  if (notes.length > 0) {
    const notesStr = notes.map(n => `${n.number || '?'}. ${esc(n.text)}`).join('; ')
    rows.push(['Notes', `general notes (${notes.length})`, notesStr])
  }

  // Surface finish
  const sf = data.surface_finish || []
  if (sf.length > 0) {
    const sfStr = sf.map(s => `Ra ${s.ra_value || '?'} ${s.unit || ''}${s.all_over ? ' (all over)' : ''}`).join('; ')
    rows.push(['Notes', `surface finish (${sf.length})`, sfStr])
  }

  // Weld symbols
  const welds = data.weld_symbols || []
  if (welds.length > 0) {
    const wStr = welds.map(w => esc(w.text_evidence || w.type || JSON.stringify(w))).join('; ')
    rows.push(['Notes', `weld symbols (${welds.length})`, wStr])
  }

  // Datum targets
  const datums = data.datum_targets || []
  if (datums.length > 0) {
    const dStr = datums.map(d => esc(d.text_evidence || d.label || JSON.stringify(d))).join('; ')
    rows.push(['Notes', `datum targets (${datums.length})`, dStr])
  }

  // Certification requirements
  const certs = data.certification_requirements || []
  if (certs.length > 0) {
    const cStr = certs.map(c => esc(c.text || c.requirement || JSON.stringify(c))).join('; ')
    rows.push(['Notes', `certification requirements (${certs.length})`, cStr])
  }

  // Notes summary envelopes
  pushEnvelopes('Notes Summary', data.notes_summary)

  // S8 BOM
  const bom = data.bom || []
  if (bom.length > 0) {
    const bomStr = bom.map(b => {
      const parts = [b.item_number, b.part_number, b.description, b.qty ? `qty:${b.qty}` : ''].filter(Boolean)
      return parts.join(' | ')
    }).join('; ')
    rows.push(['BOM', `bill of materials (${bom.length} rows)`, bomStr])
  }
  if (data.bom_title) {
    rows.push(['BOM', 'bom title', esc(data.bom_title)])
  }
  pushEnvelopes('BOM Summary', data.bom_summary)

  return rows
}

function exportXlsx(data) {
  const rows = buildCSVRows(data)
  const drawingNum = data.document_meta?.drawing_number?.value || 'export'

  const wb = new ExcelJS.Workbook()
  wb.creator = 'SourceOptima'
  wb.created = new Date()
  const ws = wb.addWorksheet('Drawing Atlas')

  // Brand colors
  const PRIMARY = '1E5045'
  const PRIMARY_LIGHT = 'EDF5F3'
  const ACCENT_ORANGE = 'F26522'
  const HEADER_BG = '0E4749'
  const WHITE = 'FFFFFF'
  const LIGHT_GRAY = 'F8FAFB'
  const BORDER_COLOR = 'E5E7EB'

  const colCount = 3

  // Row 1: Title bar (orange accent)
  ws.addRow(['SourceOptima - Drawing Atlas: ' + drawingNum])
  ws.mergeCells(1, 1, 1, colCount)
  const titleRow = ws.getRow(1)
  titleRow.height = 28
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: WHITE } }
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ACCENT_ORANGE } }
  titleRow.getCell(1).alignment = { vertical: 'middle' }

  // Row 2: Date
  ws.addRow(['Generated: ' + new Date().toISOString().split('T')[0]])
  ws.mergeCells(2, 1, 2, colCount)
  ws.getRow(2).getCell(1).font = { size: 10, color: { argb: '6B7280' } }

  // Row 3: Drawing info
  const partNum = data.identification?.part_number?.value || ''
  ws.addRow(['Drawing: ' + drawingNum + (partNum ? '  |  Part: ' + partNum : '')])
  ws.mergeCells(3, 1, 3, colCount)
  ws.getRow(3).getCell(1).font = { size: 10, italic: true, color: { argb: PRIMARY } }

  // Row 4: spacer
  ws.addRow([])

  // Row 5: Column headers
  const headerRow = ws.addRow(['Section', 'Field', 'Value'])
  headerRow.height = 22
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, size: 11, color: { argb: WHITE } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    cell.alignment = { vertical: 'middle' }
    cell.border = { bottom: { style: 'thin', color: { argb: PRIMARY } } }
  })

  // Data rows (skip header from buildCSVRows)
  let lastSection = ''
  rows.slice(1).forEach((r, rowIdx) => {
    const row = ws.addRow(r)
    const isNewSection = r[0] !== lastSection && r[0] !== ''
    lastSection = r[0] || lastSection

    row.eachCell({ includeEmpty: true }, (cell, colNum) => {
      // Section header rows get green tint
      if (isNewSection) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PRIMARY_LIGHT } }
        if (colNum === 1) cell.font = { bold: true, size: 10, color: { argb: PRIMARY } }
      } else if (rowIdx % 2 === 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_GRAY } }
      }
      cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } }
      cell.alignment = { vertical: 'top', wrapText: colNum === 3 }
    })
  })

  // Column widths
  ws.getColumn(1).width = 18
  ws.getColumn(2).width = 32
  ws.getColumn(3).width = 80

  // Freeze header
  ws.views = [{ state: 'frozen', ySplit: 5 }]

  // Download
  wb.xlsx.writeBuffer().then(buffer => {
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `SourceOptima_DrawingAtlas_${drawingNum}.xlsx`
    a.click()
    URL.revokeObjectURL(url)
  })
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function CADExtractView({ data, success, error, metadata, fileId }) {
  const [tableExpanded, setTableExpanded] = useState(false)

  if (!data) return <div className="p-6 text-gray-500">No Drawing Atlas data available.</div>

  const {
    document_meta = {},
    identification = {},
    classification = {},
    terminal_status,
    acceptance_gates = {},
    time_seconds,
    debate_rounds = 0,
    corrections_made = [],
    confidence,
    sanity_check = {},
    iterations,
  } = data

  const drawingNumber = document_meta?.drawing_number?.value || '—'
  const partNumber = identification?.part_number?.value || '—'
  const partDescription = identification?.part_description?.value || identification?.part_name?.value || ''

  const statusStyle = success
    ? 'bg-green-100 text-green-800 border-green-200'
    : 'bg-red-100 text-red-800 border-red-200'

  const gateEntries = Object.entries(acceptance_gates)
  const gatesPassed = gateEntries.filter(([, v]) => v === true).length
  const gatesTotal = gateEntries.length

  const metrics = computeMetrics(data)

  const proCorrections = corrections_made.filter(c => c?.source === 'pro_sanity')
  const debateCorrections = corrections_made.filter(c => c?.source !== 'pro_sanity')

  return (
    <div className="space-y-5 p-1">

      {/* ── HERO ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="bg-[#1e5045] px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="p-2.5 rounded-lg bg-white/10">
              <FaSearch className="w-5 h-5 text-white" />
            </div>
            <div>
              <a
                href={`/engineering-atlas?search=${encodeURIComponent(drawingNumber !== '—' ? drawingNumber : partNumber)}`}
                className="text-white/70 text-xs font-medium uppercase tracking-wider mb-0.5 hover:text-white transition-colors inline-flex items-center gap-1"
              >
                Drawing Atlas
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              </a>
              <div className="text-white text-2xl font-bold">{drawingNumber}</div>
              <div className="text-white/70 text-sm mt-0.5">{partDescription || partNumber}</div>
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${statusStyle}`}>
              {terminal_status || (success ? 'accepted' : 'rejected')}
            </span>
            <button
              onClick={() => exportXlsx(data)}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#f26522] text-white hover:bg-[#d45a1a] transition-colors"
            >
              <FaDownload size={10} />
              Export Excel
            </button>
          </div>
        </div>
      </div>

      {/* ── DRAWING PREVIEW ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FaInfoCircle className="text-[#1e5045]" size={14} />
          Drawing
          <span className="text-[10px] text-gray-400 font-normal ml-auto">Click to view full screen</span>
        </h3>
        <DrawingPreview fileId={fileId} />
        <div className="flex items-center justify-center gap-6 mt-3 text-[10px] text-gray-500">
          <span>{time_seconds != null ? `${Number(time_seconds).toFixed(1)}s` : '—'} elapsed</span>
          <span>{iterations ?? 1} iteration{iterations !== 1 ? 's' : ''}</span>
          <span>{debate_rounds} debate round{debate_rounds !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* ── RUN METRICS ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Run Quality Metrics</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Confidence gauge */}
          <ConfidenceGauge level={confidence} />

          {/* Completeness + Field distribution */}
          <div className="space-y-4">
            <CompletenessBar pct={metrics.completeness} label="Data Completeness" />

            <div>
              <span className="text-xs text-gray-600 block mb-1.5">Field Confidence Distribution</span>
              <div className="flex gap-1 h-5 rounded-full overflow-hidden bg-gray-100">
                {metrics.highCount > 0 && (
                  <div
                    className="bg-green-500 flex items-center justify-center"
                    style={{ width: `${(metrics.highCount / metrics.totalFields) * 100}%` }}
                    title={`HIGH: ${metrics.highCount}`}
                  >
                    <span className="text-[9px] text-white font-bold">{metrics.highCount}</span>
                  </div>
                )}
                {metrics.mediumCount > 0 && (
                  <div
                    className="bg-amber-400 flex items-center justify-center"
                    style={{ width: `${(metrics.mediumCount / metrics.totalFields) * 100}%` }}
                    title={`MEDIUM: ${metrics.mediumCount}`}
                  >
                    <span className="text-[9px] text-gray-800 font-bold">{metrics.mediumCount}</span>
                  </div>
                )}
                {metrics.lowCount > 0 && (
                  <div
                    className="bg-red-500 flex items-center justify-center"
                    style={{ width: `${(metrics.lowCount / metrics.totalFields) * 100}%` }}
                    title={`LOW: ${metrics.lowCount}`}
                  >
                    <span className="text-[9px] text-white font-bold">{metrics.lowCount}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-[10px] text-green-700">HIGH: {metrics.highCount}</span>
                <span className="text-[10px] text-amber-700">MED: {metrics.mediumCount}</span>
                <span className="text-[10px] text-red-700">LOW: {metrics.lowCount}</span>
              </div>
            </div>
          </div>

          {/* Stratum coverage */}
          <StratumCoveragePanel stratumCoverage={metrics.stratumCoverage} />
        </div>
      </div>

      {/* ── CORRECTIONS ── */}
      {corrections_made.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FaExclamationTriangle className="text-amber-500" size={14} />
            Corrections Applied ({corrections_made.length})
          </h3>
          <div className="space-y-3">
            {proCorrections.length > 0 && (
              <div>
                <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Pro Sanity Corrections</span>
                <div className="mt-1 space-y-1.5">
                  {proCorrections.map((c, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs">
                      <span className="font-medium text-gray-700">{c.field}</span>
                      <span className="text-gray-500 mx-2">
                        <span className="line-through text-red-600">{c.old || 'null'}</span>
                        {' → '}
                        <span className="text-green-700 font-medium">{c.new}</span>
                      </span>
                      {c.reason && <p className="text-gray-500 mt-1 text-[11px]">{c.reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {debateCorrections.length > 0 && (
              <div>
                <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Debate Corrections</span>
                <div className="mt-1 space-y-1.5">
                  {debateCorrections.map((c, i) => (
                    <div key={i} className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs">
                      <span className="font-medium text-gray-700">{typeof c === 'string' ? c : c.field || JSON.stringify(c)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ACCEPTANCE GATES ── */}
      {gatesTotal > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
          <div className="flex items-center gap-2 mb-3">
            <IoShieldCheckmark className="text-[#1e5045]" size={16} />
            <h3 className="text-sm font-semibold text-gray-800">
              Acceptance Gates — {gatesPassed}/{gatesTotal} passed
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {gateEntries.map(([name, passed]) => (
              <div key={name} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2">
                {passed === true
                  ? <FaCheckCircle className="text-green-500 flex-shrink-0" size={12} />
                  : passed === false
                    ? <FaTimesCircle className="text-red-500 flex-shrink-0" size={12} />
                    : <span className="w-3 h-3 rounded-full bg-gray-300 flex-shrink-0" />
                }
                <span className="text-gray-600 truncate">{name.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SANITY NOTES ── */}
      {sanity_check?.sanity_notes && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Pro Sanity Assessment</h3>
          <p className="text-xs text-gray-600 italic">&ldquo;{sanity_check.sanity_notes}&rdquo;</p>
          {sanity_check.warnings?.length > 0 && (
            <div className="mt-3 space-y-1">
              <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Warnings</span>
              {sanity_check.warnings.map((w, i) => (
                <div key={i} className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                  <strong>{w.field}:</strong> {w.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── FULL DATA TABLE ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <button
          onClick={() => setTableExpanded(prev => !prev)}
          className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <FaInfoCircle className="text-[#1e5045]" size={14} />
            Complete Extraction Data
          </h3>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${tableExpanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {tableExpanded && (
          <div className="border-t border-gray-100 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2 font-semibold text-gray-600 w-32">Section</th>
                  <th className="px-4 py-2 font-semibold text-gray-600 w-48">Field</th>
                  <th className="px-4 py-2 font-semibold text-gray-600">Value</th>
                </tr>
              </thead>
              <tbody>
                {buildCSVRows(data).slice(1).map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="px-4 py-1.5 text-gray-500 font-medium align-top whitespace-nowrap">{row[0]}</td>
                    <td className="px-4 py-1.5 text-gray-600 align-top whitespace-nowrap">{row[1]}</td>
                    <td className="px-4 py-1.5 text-gray-800 break-words max-w-md">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  )
}
